'use client';
import {useCallback, useEffect, useRef, useState} from "react";
import { checkApproval } from "@/services/authService";
import { AuthenticationResultStatus } from "@/types/api";
import { ApprovalRequest, ApprovalResponse } from "@/types/auth";

export function useWaitingForApproval() {
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const POLLING_INTERVAL: number = 3000; // three seconds
    const MAX_POLLING_INTERVAL: number = process.env.NEXT_PUBLIC_TIMEOUT_DURATION ? parseInt(process.env.NEXT_PUBLIC_TIMEOUT_DURATION) * 1000 : 180000;
    const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
    const [success, setSuccess] = useState(false);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string|undefined>();
    const [startTime, setStartTime] = useState<number>(0);

    const startPolling = useCallback((approvalRequest: ApprovalRequest): void => {
        const now = Date.now();
        setStartTime(now);
        setIsWaitingForApproval(true);
        setHasTimedOut(false);
        setStatusMessage("");

        const poll = async () => {
            try {
                const data: ApprovalResponse = await checkApproval(approvalRequest);
                if (data.status === AuthenticationResultStatus.SUCCESS) {
                    setSuccess(true);
                    setIsWaitingForApproval(false);
                    return;
                } else if (data.status === AuthenticationResultStatus.FAILED) {
                    setSuccess(false);
                    setIsWaitingForApproval(false);
                    setStatusMessage(data.statusMessage);
                    return;
                }
            } catch (err) {
                console.error(err);
                setSuccess(false);
                setIsWaitingForApproval(false);
                setStatusMessage("Unexpected error occurred. Please try again later.");
                return;
            }

            pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
        };

        poll();
    }, [POLLING_INTERVAL]);

    const handleTimeout = useCallback(() => {
        setSuccess(false);
        setIsWaitingForApproval(false);
        setHasTimedOut(true);
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        setSuccess(false);
        setIsWaitingForApproval(false);
        setHasTimedOut(false);
        setStatusMessage(undefined);
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
            setIsWaitingForApproval(false);
            setSuccess(false);
            setHasTimedOut(false);
        };
    }, []);

    return { 
        startPolling, 
        isWaitingForApproval, 
        success, 
        statusMessage, 
        hasTimedOut, 
        MAX_POLLING_INTERVAL,
        startTime,
        handleTimeout,
        reset
    };
}
